import React, { useCallback } from 'react';
import { Box } from '@mui/material';

import { StyledTab,StyledTabs } from './SegmentedControl.styles';
import type { SegmentedControlProps } from './SegmentedControl.types';

/**
 * iOS-style segmented control component with sliding animation
 * Uses MUI Tabs under the hood with custom styling
 */
export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  size = 'medium',
  disabled = false,
  className,
  ariaLabel,
}) => {
  // Handle tab change
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string) => {
      if (!disabled) {
        onChange(newValue);
      }
    },
    [onChange, disabled]
  );

  // Find the index of the current value
  const currentIndex = options.findIndex((option) => option.value === value);
  const tabValue = currentIndex !== -1 ? currentIndex : 0;

  return (
    <Box className={className}>
      <StyledTabs
        value={tabValue}
        onChange={(_e, newIndex) => {
          if (typeof newIndex === 'number' && options[newIndex]) {
            handleChange(_e, options[newIndex].value);
          }
        }}
        aria-label={ariaLabel || 'Segmented control'}
        size={size}
        TabIndicatorProps={{
          style: {
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      >
        {options.map((option, index) => (
          <StyledTab
            key={option.value}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {option.icon}
                <span>{option.label}</span>
              </Box>
            }
            value={index}
            size={size}
            disabled={disabled}
            disableRipple
          />
        ))}
      </StyledTabs>
    </Box>
  );
};

export default SegmentedControl;